import { useEffect, useMemo, useReducer } from 'react'
import {
    Autocomplete,
    Button,
    TextField,
    FormControlLabel,
    Chip,
    Tooltip,
    Switch,
    debounce,
} from '@mui/material'
import {
    Star,
    StarBorderOutlined,
    Save as SaveIcon,
    RestartAlt as RestartAltIcon,
} from '@mui/icons-material'
import {
    SaveButton,
    Grid,
    FormSection,
    UniversalGoogleMapsAc,
    Autocomplete as UniversalAutocomplete,
    UniversalAccordion,
} from '../../../UniversalComponents'
import RoleBadge from '../RoleBadge'

import { useToastMessage } from '../../../../context/ToastPopUpContext'
import { useMinimizer } from '../../../../context/MinimizeContext'
import { useAuth } from '../../../../context/AuthenticationContext'
import { useNavMenu } from '../../../../context/NavMenuContext'
import responseError from '../../../../context/responseError/responseError'
import {
    editUserPassword,
    getDealers,
    getUserProfiles,
    getManufacturers,
    addUser,
    editUser,
    getBoats,
    getUserAssignedBoats,
    getDealerProfile,
} from '../../../../apiRoutes'

import UserProfileForm from '../UserProfiles/UserProfileForm'
import parsePhoneNumber, { AsYouType } from 'libphonenumber-js'

interface UserFormFormData {
    id?: string
    _id?: string
    userEmail: string
    userFirstName?: string
    userLastName?: string
    userPhoneNumber?: string
    userAddress?: string
    updatedBy?: string
    updatedAt?: string
    activeManufacturer: { manufacturerName: '' }
    manufacturer?: []
    userProfile: object
    userPermissions?: string
    disableDeactivation: boolean
    userBoats?: [
        {
            nickname: ''
            fullHin: ''
            _id: ''
        }
    ]
    favoriteBoat?: {}
    assignedDealerProfile: { dealerProfileName: '' }
    userPreferences: {
        imperial: boolean
    }
    userNotificationPreferences: {
        userAppNotifications: {
            notifyUserChanges: boolean
            notifyBoatChanges: boolean
            notifyBoatSignal: boolean
            notifyGeofencing: boolean
            notifyByDealer: boolean
        }

        userEmailNotifications: {
            notifyUserChanges: boolean
            notifyBoatChanges: boolean
            notifyBoatSignal: boolean
            notifyGeofencing: boolean
            notifyByDealer: boolean
        }
        userSmsNotifications: {
            notifyUserChanges: boolean
            notifyBoatChanges: boolean
            notifyBoatSignal: boolean
            notifyGeofencing: boolean
        }
    }
}
interface UserFormTypes {
    row: UserFormFormData
    formDescription?: string
    userFormPermissionCheck?: boolean

    onClose?: any
    saveCache?: any
    minimizeModal?: () => void
    userAccount?: boolean
    disableDeactivation?: boolean
}

const UserForm = ({
    row,
    formDescription,
    onClose = () => {},
    saveCache = () => {},
    minimizeModal,
    userAccount = false,

    userFormPermissionCheck,
}: UserFormTypes) => {
    const setToastMessage = useToastMessage()
    const { setMinimized, restoredData } = useMinimizer()
    const currentUser = useAuth()
    const { navMenuLinks } = useNavMenu()
    const auth: any = currentUser.auth

    const userNotificationPreferencesDefaults = {
        userAppNotifications: {
            notifyUserChanges: true,
            notifyBoatChanges: true,
            notifyBoatSignal: true,
            notifyGeofencing: true,
            notifyByDealer: true,
        },

        userEmailNotifications: {
            notifyUserChanges: true,
            notifyBoatChanges: false,
            notifyBoatSignal: false,
            notifyGeofencing: false,
            notifyByDealer: true,
        },
        userSmsNotifications: {
            notifyUserChanges: false,
            notifyBoatChanges: false,
            notifyBoatSignal: false,
            notifyGeofencing: false,
        },
    }
    const defaultState = {
        formData: {
            id: row._id || '',
            userEmail: row.userEmail || '',
            userFirstName: row.userFirstName || '',
            userLastName: row.userLastName || '',
            userPhoneNumber: row.userPhoneNumber || '',
            userAddress: row?.userAddress || '',
            password: '',
            manufacturer: row.manufacturer || [],
            activeManufacturer: row.activeManufacturer?.manufacturerName || '',
            userProfile: row?.userProfile || {},
            userPermissions: row.userPermissions || {},
            userBoats: row.userBoats || [],
            favoriteBoat: row.favoriteBoat || null,
            userPreferences: row?.userPreferences || { imperial: false },
            assignedDealerProfile:
                row?.assignedDealerProfile?.dealerProfileName || '',
            // Merge stored prefs over defaults per channel so newly-added keys
            // (e.g. notifyByDealer) always render, without clobbering saved values.
            userNotificationPreferences: {
                userAppNotifications: {
                    ...userNotificationPreferencesDefaults.userAppNotifications,
                    ...(row?.userNotificationPreferences
                        ?.userAppNotifications || {}),
                },
                userEmailNotifications: {
                    ...userNotificationPreferencesDefaults.userEmailNotifications,
                    ...(row?.userNotificationPreferences
                        ?.userEmailNotifications || {}),
                },
                userSmsNotifications: {
                    ...userNotificationPreferencesDefaults.userSmsNotifications,
                    ...(row?.userNotificationPreferences
                        ?.userSmsNotifications || {}),
                },
            },
            disableDeactivation: row?.disableDeactivation || false,
        },
        formValidation: {
            assignedDealerValidation: false,
            userEmailValidation: false,
            userFirstNameValidation: false,
            userLastNameValidation: false,
            userPhoneNumberValidation: false,
            userProfileValidation: false,
            passwordValidation: false,
            manufacturerValidation: false,
            activeManufacturerValidation: false,
            newPasswordValidation: false,
            passwordConfirmValidation: false,
            isFormDirty: false,
            isFormValid: false,
        },
        isFormDirty: false,
        isFormValid: false,
        closeModal: false,
        manufacturerNames: [],
        manufacturers: [],
        userProfiles: [],
        fleet: [],
        newPassword: '',
        passwordConfirm: '',
        email: row.userEmail || '',
        passwordSubmitDisabled: true,
        dealerProfiles: [],
        dealerProfileOptions: [],
        assignedManufacturersOptions: [],
        assignedManufacturers: [],
        phoneNumberError: false,
        userAssignedBoatSearch: '',
    }

    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)

    const {
        formData,
        manufacturerNames,
        manufacturers,
        formValidation,
        fleet,
        isEditMode,
        setIsEditMode,
        assignedManufacturersOptions,
        assignedManufacturers,
        userProfiles,
        newPassword,
        passwordConfirm,
        email,
        passwordSubmitDisabled,
        dealerProfiles,
        dealerProfileOptions,
        phoneNumberError,
        userAssignedBoatSearch,
    } = state

    const {
        userEmailValidation,
        userFirstNameValidation,
        userLastNameValidation,
        userPhoneNumberValidation,
        userProfileValidation,
        manufacturerValidation,
        activeManufacturerValidation,
        newPasswordValidation,
        passwordConfirmValidation,
        passwordValidation,
        isFormDirty,
        isFormValid,
        assignedDealerValidation,
    } = formValidation

    const {
        id,
        userEmail,
        userFirstName,
        userLastName,
        userPhoneNumber,
        password,
        activeManufacturer,
        manufacturer,
        userPermissions,
        userAddress,
        userBoats,
        favoriteBoat,
        userPreferences,
        userNotificationPreferences,
        userProfile,
        assignedDealerProfile,
        disableDeactivation,
    } = formData

    const { imperial } = userPreferences

    const handleSubmit = async () => {
        try {
            const findActiveManufacturer = assignedManufacturers?.find(
                ({ manufacturerName }) =>
                    manufacturerName === activeManufacturer
            )
            const findDealerProfile = dealerProfiles.find(
                ({ dealerProfileName }) =>
                    dealerProfileName === assignedDealerProfile
            )

            const findUserBoats = (userBoats || [])
                .map((boat) => boat._id)
                .filter(Boolean)

            const findFavoriteBoat = favoriteBoat?._id || null

            let parsedFormData = {
                ...formData,
                activeManufacturer: findActiveManufacturer,
                assignedDealerProfile: findDealerProfile?._id || null,
                userBoats: findUserBoats,
                favoriteBoat: findFavoriteBoat,
            }
            let res: any

            if (row.id || userAccount) {
                res = await editUser(parsedFormData)
                if (res) {
                    const { data, status } = res

                    if (data && status === 200) {
                        setToastMessage(
                            `User: ${data.userEmail} was edited successfully`,
                            'success'
                        )
                        userAccount
                            ? setState({
                                  formValidation: {
                                      ...formValidation,
                                      isFormDirty: false,
                                  },
                              })
                            : null

                        onClose(row.userEmail, data)
                    } else {
                        responseError(res, row)
                    }
                }
            } else {
                res = await addUser(parsedFormData)

                if (res) {
                    const { data, status } = res

                    if (data?.status === 'failed') {
                        setToastMessage(`User: ${data?.message}`, 'error')
                    } else if (data && status === 200) {
                        setToastMessage(
                            `User: ${data.userEmail} was added successfully`,
                            'success'
                        )
                        onClose(row.userEmail, data)
                    } else {
                        responseError(res, row)
                    }
                }
            }
        } catch (err) {
            console.error(err, 'error')
            setToastMessage(`Admin password is incorrect.`, 'error')
        }
    }

    const handlePasswordSubmit = async () => {
        try {
            let newFormData = { email, password: newPassword }
            let parsedFormData = { ...newFormData }
            let res: any

            res = await editUserPassword(parsedFormData)
            if (res) {
                const { data, status } = res

                if (data && status === 200) {
                    setToastMessage(
                        `Password has been saved successfully`,
                        'success'
                    )

                    setState({
                        newPassword: '',
                        passwordConfirm: '',
                        passwordSubmitDisabled: true,
                        formValidation: {
                            ...formValidation,
                            passwordConfirmValidation: false,
                            newPasswordValidation: false,
                        },
                    })
                } else {
                    responseError(res, row)
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleReset = () =>
        setState({
            formData: defaultState.formData,
            phoneNumberError: defaultState.phoneNumberError,

            formValidation: defaultState.formValidation,
        })

    const resetPasswordFields = () =>
        setState({
            newPassword: '',
            passwordConfirm: '',
            passwordSubmitDisabled: true,
            formValidation: {
                ...formValidation,
                passwordConfirmValidation: false,
                newPasswordValidation: false,
            },
        })

    const handleChange = (e: any) => {
        const { name, value } = e.target
        if (name === 'userProfile') {
            const selectedProfile = userProfiles.find(
                (userProfile) => userProfile.userProfileName === value
            )
            setState({
                formData: {
                    ...formData,
                    [name]: selectedProfile,
                    activeManufacturer: '',
                    assignedDealerProfile: selectedProfile?.dealerProfile
                        ? assignedDealerProfile
                        : null,
                },
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
            })
        } else if (name === 'newPassword') {
            setState({ [name]: value })
        } else if (name === 'passwordConfirm') {
            setState({ [name]: value })
        } else {
            setState({
                formData: { ...formData, [name]: value },
            })
        }
    }

    useEffect(() => {
        const emailValid = () => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return emailRegex.test(userEmail)
        }
        const isPasswordNeeded = row?._id
            ? true
            : password && !passwordValidation

        if (
            isFormDirty &&
            !userEmailValidation &&
            emailValid() &&
            !userFirstNameValidation &&
            !userLastNameValidation &&
            !manufacturerValidation &&
            !activeManufacturerValidation &&
            !userProfileValidation &&
            userEmail &&
            userFirstName &&
            userLastName &&
            isPasswordNeeded &&
            userProfile?.userProfileName &&
            activeManufacturer
        ) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: false,
                },
            })
        }
    }, [
        isFormDirty,
        userEmailValidation,
        userFirstNameValidation,
        userLastNameValidation,

        userProfileValidation,
        activeManufacturerValidation,
        userEmail,
        userFirstName,
        userLastName,

        activeManufacturer,
        userProfile,
        password,
    ])

    const validateOnBlur = (e: any) => {
        const { name, value } = e.target

        if (value !== '') {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: false,
                    isFormDirty: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: true,
                    isFormDirty: true,
                },
            })
        }
    }

    const validatePasswordOnBlur = (e: any) => {
        const { name, value } = e.target

        if (name === 'newPassword' && value?.length > 5) {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: true,
                },
            })
        } else if (
            name === 'passwordConfirm' &&
            value === newPassword &&
            value?.length > 5
        ) {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: false,
                },
            })
        }
    }

    const getData = async () => {
        let manufacturerNames: any = []
        let manufacturers: any = []
        let dealerProfiles: any = []
        let fleet: any = []
        let userProfiles: any = []
        let dealerProfileOptions: any = []

        try {
            let data = await getManufacturers(true)
            let fleetData = await getUserAssignedBoats({ active: true })
            const getDealerData = await getDealers(true)
            let userProfilesData = await getUserProfiles(true)
            const getDealerProfileData = await getDealerProfile(true)

            if (data && data?.length !== 0) {
                manufacturerNames = data.map(
                    ({ manufacturerName }) => manufacturerName
                )
                manufacturers = data.map(({ _id, manufacturerName }) => ({
                    _id,
                    manufacturerName,
                }))
            }
            if (fleetData && fleetData?.length !== 0) {
                fleet = fleetData?.map((boat) => ({
                    nickname: boat?.nickname,
                    fullHin: boat?.fullHin,
                    shortHin: boat?.shortHin,
                    _id: boat?._id,

                    label: boat?.nickname
                        ? `${boat?.fullHin || ''} - ${boat?.nickname || ''}`
                        : boat?.fullHin,
                }))
            }
            if (userProfilesData && userProfilesData?.length !== 0) {
                userProfiles = userProfilesData.map(
                    (userProfile) => userProfile
                )
            }
            if (getDealerProfileData && getDealerProfileData.length !== 0) {
                dealerProfiles = getDealerProfileData
                dealerProfileOptions = dealerProfiles.map(
                    (dp) => dp?.dealerProfileName
                )
            }

            setState({
                manufacturerNames,
                manufacturers,
                dealerProfiles,
                dealerProfileOptions,
                fleet,
                userProfiles,
            })
        } catch (err: any) {
            setToastMessage(err, 'error')
        }
    }

    useEffect(() => {
        if (!userProfile) return

        let assignedManufacturers: any
        let assignedManufacturersOptions: any

        if (userProfile.dealerProfile === true) {
            const findDealerProfile = dealerProfiles?.find(
                ({ dealerProfileName }) =>
                    dealerProfileName === assignedDealerProfile
            )
            assignedManufacturers = findDealerProfile?.manufacturers

            assignedManufacturersOptions = assignedManufacturers?.map(
                (manufacturer) => manufacturer?.manufacturerName
            )
        } else {
            assignedManufacturers =
                userProfile?.userProfileAssignedManufacturers

            assignedManufacturersOptions = assignedManufacturers?.map(
                ({ manufacturerName }) => manufacturerName
            )
        }

        setState({ assignedManufacturers, assignedManufacturersOptions })
    }, [userProfile, dealerProfiles, assignedDealerProfile, activeManufacturer])

    const handleUserPermissionsChange = (bool, name, type) => {
        setState({
            formValidation: {
                ...formValidation,
                isFormDirty: true,
            },
            formData: {
                ...formData,
                userPermissions: {
                    ...userPermissions,
                    [name]: {
                        ...userPermissions[name],
                        [type]: bool,
                        read: bool ? true : false,
                    },
                },
            },
        })
    }

    useEffect(() => {
        getData()
        saveCache(row)
    }, [])

    useEffect(() => {
        if (restoredData?.id !== undefined) {
            setState({ row: restoredData, open: true })
        }
    }, [restoredData])

    useEffect(() => {
        if (
            newPassword === passwordConfirm &&
            newPassword !== '' &&
            newPassword?.length > 5
        ) {
            setState({
                passwordSubmitDisabled: false,
                formValidation: {
                    ...formValidation,
                    passwordConfirmValidation: true,
                    newPasswordValidation: true,
                },
            })
        } else {
            setState({
                passwordSubmitDisabled: true,
                formValidation: {
                    ...formValidation,
                    passwordConfirmValidation: false,
                    newPasswordValidation: false,
                },
            })
        }
    }, [newPassword, passwordConfirm])

    const debouncedSearch = useMemo(
        () =>
            debounce((value: string) => {
                setState({ userAssignedBoatSearch: value })
            }, 600),
        []
    )

    useEffect(() => {
        const getSearchedBoats = async () => {
            try {
                const searchedBoats = await getUserAssignedBoats({
                    active: true,
                    search: userAssignedBoatSearch,
                    limit: 20,
                })

                if (!searchedBoats || searchedBoats.length === 0) {
                    setState({ fleet: [] })
                    return
                }

                const searchResults = searchedBoats.map((boat) => ({
                    _id: boat._id,
                    nickname: boat.nickname,
                    fullHin: boat.fullHin,
                    shortHin: boat.shortHin,
                    label: boat.nickname
                        ? `${boat.fullHin || ''} - ${boat.nickname || ''}`
                        : boat.fullHin,
                }))

                const existingIds = new Set(
                    (userBoats || []).map((boat) => boat._id)
                )

                const finalResults = searchResults.filter(
                    (boat) => !existingIds.has(boat._id)
                )

                setState({ fleet: finalResults })
            } catch (err: any) {
                setToastMessage(err, 'error')
            }
        }

        getSearchedBoats()
    }, [userAssignedBoatSearch])

    const emailCheck = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(userEmail)
    }

    const handleUserPreferencesSubmit = async () => {
        try {
            let parsedFormData = { ...formData, ...row, userPreferences: {} }

            let res: any

            if (row.id || userAccount) {
                res = await editUser(parsedFormData)
                if (res) {
                    const { data, status } = res

                    if (data && status === 200) {
                        setToastMessage(
                            `User: ${data.userEmail} was edited successfully`,
                            'success'
                        )
                        setState({
                            formData: {
                                ...formData,
                                userPreferences: { imperial: false },
                            },
                            formValidation: {
                                ...formValidation,
                                isFormDirty: false,
                            },
                        })
                    } else {
                        responseError(res, row)
                    }
                }
            }
        } catch (err) {
            console.error(err, 'error')
            setToastMessage(`Admin password is incorrect.`, 'error')
        }
    }

    const handleResetUserPreferences = () =>
        setToastMessage(
            'Are you sure you want to reset user preferences?',
            'warning',
            handleUserPreferencesSubmit
        )

    const profileCheck = auth?.username === userEmail

    useEffect(() => {
        const parsedInfo = parsePhoneNumber(userPhoneNumber, {
            defaultCountry: 'US',
            extract: false,
        })

        const country = parsedInfo?.country || 'US'

        const parsedNumber = new AsYouType(country).input(userPhoneNumber)
        const isValid = parsedInfo?.isValid()

        if (userPhoneNumber && userPhoneNumber !== '') {
            setState({
                formData: { ...formData, userPhoneNumber: parsedNumber },
                phoneNumberError: !isValid,
            })
        }
    }, [userPhoneNumber])

    const userEditingPermission = auth?.userPermissions?.User?.write === true
    const superAdmin = auth?.userProfile.superAdmin === true

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sx={{ marginTop: 5 }}>
                <Grid container spacing={2}>
                    <Grid
                        item
                        xs={12}
                        md={
                            auth?.userProfile?.superAdmin &&
                            auth?.userProfile?.superAdmin === true
                                ? 7
                                : 12
                        }
                    >
                        <FormSection
                            title="User Information"
                            titleAlignment="center"
                        >
                            {(row?.id || row?._id) && (row as any)?.role && (
                                <Grid
                                    item
                                    xs={12}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mb: 0.5,
                                    }}
                                >
                                    <RoleBadge role={(row as any).role} />
                                </Grid>
                            )}
                            <Grid item md={row.id || row._id ? 12 : 7} xs={12}>
                                <TextField
                                    required={true}
                                    label="E-Mail"
                                    name="userEmail"
                                    fullWidth
                                    onChange={handleChange}
                                    value={userEmail}
                                    onBlur={validateOnBlur}
                                    disabled={userFormPermissionCheck}
                                    error={
                                        userEmailValidation === false &&
                                        userEmail
                                            ? !emailCheck()
                                            : userEmailValidation && !userEmail
                                    }
                                    helperText={
                                        userEmailValidation === false &&
                                        !userEmail
                                            ? userEmailValidation && !userEmail
                                            : !emailCheck()
                                            ? 'Please enter a valid Email Address.'
                                            : ''
                                    }
                                />
                            </Grid>

                            {!row.id && !row._id && (
                                <Grid item xs={5}>
                                    <TextField
                                        required={true}
                                        label="Enter users temporary password."
                                        name="password"
                                        fullWidth
                                        onChange={handleChange}
                                        value={password}
                                        type="password"
                                        onBlur={validateOnBlur}
                                        disabled={userFormPermissionCheck}
                                        error={passwordValidation && !password}
                                        helperText={
                                            passwordValidation && !password
                                                ? 'Please enter a password'
                                                : ''
                                        }
                                    />
                                </Grid>
                            )}
                            <Grid item md={4} xs={12}>
                                <TextField
                                    required={true}
                                    fullWidth
                                    label="First Name"
                                    name="userFirstName"
                                    onChange={handleChange}
                                    value={userFirstName}
                                    onBlur={validateOnBlur}
                                    error={
                                        userFirstNameValidation &&
                                        !userFirstName
                                    }
                                    helperText={
                                        userFirstNameValidation &&
                                        !userFirstName
                                            ? 'Please enter a First Name'
                                            : ''
                                    }
                                    disabled={userFormPermissionCheck}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField
                                    required={true}
                                    fullWidth
                                    label="Last Name"
                                    name="userLastName"
                                    onChange={handleChange}
                                    value={userLastName}
                                    onBlur={validateOnBlur}
                                    error={
                                        userLastNameValidation && !userLastName
                                    }
                                    helperText={
                                        userLastNameValidation && !userLastName
                                            ? 'Please enter a Last Name'
                                            : ''
                                    }
                                    disabled={userFormPermissionCheck}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    name="userPhoneNumber"
                                    onChange={handleChange}
                                    value={userPhoneNumber}
                                    error={
                                        (userPhoneNumberValidation &&
                                            !userPhoneNumber) ||
                                        (phoneNumberError &&
                                            userPhoneNumber?.length !== '')
                                    }
                                    helperText={
                                        phoneNumberError &&
                                        userPhoneNumber?.length !== ''
                                            ? 'Please Enter a Valid Phone Number'
                                            : userPhoneNumberValidation &&
                                              !userPhoneNumber
                                            ? 'Please enter a Phone Number for the User'
                                            : ''
                                    }
                                    disabled={userFormPermissionCheck}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <UniversalGoogleMapsAc
                                    name="userAddress"
                                    value={userAddress}
                                    label="Address"
                                    fullWidth={true}
                                    clearFunction={() =>
                                        setState({
                                            formValidation: {
                                                ...formValidation,
                                                isFormDirty: true,
                                            },
                                            formData: {
                                                ...formData,
                                                userAddress: '',
                                            },
                                        })
                                    }
                                    onChange={(event) => {
                                        if (event?.address) {
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    userAddress: event.address,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                    }}
                                    disabled={userFormPermissionCheck}
                                />
                            </Grid>
                        </FormSection>
                        <FormSection
                            title="User Preferences"
                            titleAlignment="center"
                        >
                            <Grid item xs={12} md={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            onChange={() =>
                                                setState({
                                                    formData: {
                                                        ...formData,
                                                        userPreferences: {
                                                            ...userPreferences,
                                                            imperial: !imperial,
                                                        },
                                                    },
                                                    formValidation: {
                                                        ...formValidation,
                                                        isFormDirty: true,
                                                    },
                                                })
                                            }
                                            inputProps={{
                                                'aria-label': 'controlled',
                                            }}
                                            id="switch.imperial"
                                            name="switch.imperial"
                                            checked={imperial}
                                            key="switch.imperial"
                                        />
                                    }
                                    label={
                                        !imperial
                                            ? 'Metric Measurements'
                                            : 'Imperial Measurements'
                                    }
                                    key="switch.imperial"
                                    disabled={userFormPermissionCheck}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={disableDeactivation}
                                            onChange={(e) =>
                                                setState({
                                                    formData: {
                                                        ...formData,
                                                        disableDeactivation:
                                                            e.target.checked,
                                                    },
                                                    formValidation: {
                                                        ...formValidation,
                                                        isFormDirty: true,
                                                    },
                                                })
                                            }
                                        />
                                    }
                                    label="Deactivation Protection"
                                    key="switch.mobileCustomerProfile"
                                />
                            </Grid>
                            {(row.id || userAccount) && (
                                <Grid item xs={12} md={12}>
                                    <Button
                                        onClick={handleResetUserPreferences}
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        sx={{
                                            height: 55,
                                        }}
                                        disabled={userFormPermissionCheck}
                                    >
                                        Reset User Preferences
                                    </Button>
                                </Grid>
                            )}
                        </FormSection>
                        <FormSection
                            title="User Notification Preferences"
                            titleAlignment="center"
                        >
                            {userNotificationPreferences &&
                                Object.keys(userNotificationPreferences).map(
                                    (key) => (
                                        <UniversalAccordion
                                            accordionTitle={
                                                (key.toString() ===
                                                    'userAppNotifications' &&
                                                    'App Notifications') ||
                                                (key.toString() ===
                                                    'userEmailNotifications' &&
                                                    'Email Notifications') ||
                                                (key.toString() ===
                                                    'userSmsNotifications' &&
                                                    'SMS Notifications') ||
                                                ''
                                            }
                                            disabled={userFormPermissionCheck}
                                            accordionTitleDescription={
                                                (key.toString() ===
                                                    'userAppNotifications' &&
                                                    'Select what notifications you recieve in App.') ||
                                                (key.toString() ===
                                                    'userEmailNotifications' &&
                                                    'Select what notifications you recieve in Email.') ||
                                                (key.toString() ===
                                                    'userSmsNotifications' &&
                                                    'Select what notifications you recieve in App.') ||
                                                ''
                                            }
                                        >
                                            <Grid
                                                container
                                                spacing={2}
                                                sx={{ marginLeft: 5 }}
                                            >
                                                {key &&
                                                    Object.keys(
                                                        userNotificationPreferences[
                                                            key
                                                        ]
                                                    ).map(
                                                        (notificationType) => (
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                md={6}
                                                            >
                                                                <FormControlLabel
                                                                    control={
                                                                        <Switch
                                                                            onChange={() =>
                                                                                setState(
                                                                                    {
                                                                                        formData:
                                                                                            {
                                                                                                ...formData,
                                                                                                userNotificationPreferences:
                                                                                                    {
                                                                                                        ...userNotificationPreferences,
                                                                                                        [key]: {
                                                                                                            ...userNotificationPreferences[
                                                                                                                key
                                                                                                            ],
                                                                                                            [notificationType]:
                                                                                                                !userNotificationPreferences[
                                                                                                                    key
                                                                                                                ][
                                                                                                                    notificationType
                                                                                                                ],
                                                                                                        },
                                                                                                    },
                                                                                            },
                                                                                        formValidation:
                                                                                            {
                                                                                                ...formValidation,
                                                                                                isFormDirty:
                                                                                                    true,
                                                                                            },
                                                                                    }
                                                                                )
                                                                            }
                                                                            inputProps={{
                                                                                'aria-label':
                                                                                    'controlled',
                                                                            }}
                                                                            id={`switch.${key}-${userNotificationPreferences[key][notificationType]}`}
                                                                            name={`${userNotificationPreferences[key][notificationType]}`}
                                                                            checked={
                                                                                userNotificationPreferences[
                                                                                    key
                                                                                ][
                                                                                    notificationType
                                                                                ]
                                                                            }
                                                                            key={`switch.${key}-${userNotificationPreferences[key][notificationType]}`}
                                                                        />
                                                                    }
                                                                    label={
                                                                        (notificationType ===
                                                                            'notifyUserChanges' &&
                                                                            'Notify User Changes') ||
                                                                        (notificationType ===
                                                                            'notifyBoatChanges' &&
                                                                            'Notify Boat Changes') ||
                                                                        (notificationType ===
                                                                            'notifyBoatSignal' &&
                                                                            'Notify Boat Signals') ||
                                                                        (notificationType ===
                                                                            'notifyGeofencing' &&
                                                                            'Notify Geofencing') ||
                                                                        (notificationType ===
                                                                            'notifyByDealer' &&
                                                                            'Notifications from Dealer')
                                                                    }
                                                                    key={`switch.${notificationType}`}
                                                                />
                                                            </Grid>
                                                        )
                                                    )}
                                            </Grid>
                                        </UniversalAccordion>
                                    )
                                )}
                        </FormSection>
                        {(userEditingPermission ||
                            superAdmin ||
                            userAccount) && (
                            <FormSection
                                title={
                                    auth?.userProfile?.dealerProfile === true
                                        ? 'Dealer Assigned Boats'
                                        : 'User Assigned Boats'
                                }
                                titleAlignment="center"
                            >
                                <Grid item md={12} xs={12}>
                                    <Autocomplete
                                        multiple
                                        id="userBoats"
                                        value={userBoats}
                                        onBlur={() =>
                                            setState({
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                        onChange={(event, newValue) => {
                                            const data = {
                                                target: {
                                                    name: 'userBoats',
                                                    value: newValue,
                                                },
                                            }

                                            handleChange(data)
                                        }}
                                        options={fleet}
                                        disabled={
                                            userFormPermissionCheck ||
                                            // On the user's own account page,
                                            // show assigned boats read-only
                                            // unless they can edit users.
                                            (userAccount &&
                                                !userEditingPermission &&
                                                !superAdmin)
                                        }
                                        getOptionLabel={(option) =>
                                            option.label
                                        }
                                        onInputChange={(e, newValue) => {
                                            debouncedSearch(newValue)
                                        }}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option._id}>
                                                {option.label}
                                            </li>
                                        )}
                                        renderTags={(tagValue, getTagProps) =>
                                            tagValue.map(
                                                (option: any, index) => (
                                                    <Tooltip
                                                        title={
                                                            favoriteBoat &&
                                                            favoriteBoat._id ===
                                                                option?._id
                                                                ? 'Click to unfavorite boat.'
                                                                : 'Click to favorite boat.'
                                                        }
                                                        key={`${option._id}-tooltip`}
                                                    >
                                                        <Chip
                                                            {...getTagProps({
                                                                index,
                                                            })}
                                                            label={
                                                                option.shortHin
                                                            }
                                                            variant={
                                                                favoriteBoat &&
                                                                favoriteBoat._id ===
                                                                    option?._id
                                                                    ? 'filled'
                                                                    : 'outlined'
                                                            }
                                                            onDelete={(e) => {
                                                                getTagProps({
                                                                    index,
                                                                }).onDelete(e)
                                                                setState({
                                                                    formValidation:
                                                                        {
                                                                            ...formValidation,
                                                                            isFormDirty:
                                                                                true,
                                                                        },
                                                                })
                                                            }}
                                                            icon={
                                                                favoriteBoat &&
                                                                favoriteBoat._id ===
                                                                    option?._id ? (
                                                                    <Star />
                                                                ) : (
                                                                    <StarBorderOutlined />
                                                                )
                                                            }
                                                            disabled={
                                                                fleet.indexOf(
                                                                    index
                                                                ) !== -1
                                                            }
                                                            onClick={() =>
                                                                setState({
                                                                    formData: {
                                                                        ...formData,
                                                                        favoriteBoat:
                                                                            favoriteBoat &&
                                                                            favoriteBoat._id ===
                                                                                option?._id
                                                                                ? {}
                                                                                : option,
                                                                    },
                                                                })
                                                            }
                                                        />
                                                    </Tooltip>
                                                )
                                            )
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                name="userBoats"
                                                label={
                                                    auth?.userProfile
                                                        ?.dealerProfile === true
                                                        ? 'Dealer Assigned Boats'
                                                        : 'Assigned Boats'
                                                }
                                                placeholder="Assigned Boats"
                                            />
                                        )}
                                    />
                                </Grid>
                            </FormSection>
                        )}
                        {((profileCheck && id !== '') ||
                            (auth?.userProfile?.superAdmin === true &&
                                id !== '')) && (
                            <FormSection
                                title="Update Login Password"
                                titleAlignment="left"
                            >
                                <UniversalAccordion
                                    title="Change Password"
                                    titleAlignment="center"
                                    accordionTitle={email}
                                    accordionTitleDescription="Update Password"
                                >
                                    <Grid item md={12} xs={12}>
                                        <Grid container spacing={2}>
                                            <Grid item md={12} xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="User Email"
                                                    name="email"
                                                    onChange={handleChange}
                                                    value={email}
                                                    disabled={true}
                                                />
                                            </Grid>
                                            <Grid item md={6} xs={12}>
                                                <TextField
                                                    required={true}
                                                    fullWidth
                                                    label="New Password"
                                                    name="newPassword"
                                                    onChange={handleChange}
                                                    value={newPassword}
                                                    onBlur={
                                                        validatePasswordOnBlur
                                                    }
                                                    error={
                                                        !newPasswordValidation &&
                                                        newPassword?.length <
                                                            6 &&
                                                        newPassword !== ''
                                                    }
                                                    helperText={
                                                        !newPasswordValidation &&
                                                        newPassword?.length <
                                                            6 &&
                                                        newPassword !== ''
                                                            ? 'Password needs to be longer then 6 Characters'
                                                            : ''
                                                    }
                                                />
                                            </Grid>
                                            <Grid item md={6} xs={12}>
                                                <TextField
                                                    required={true}
                                                    fullWidth
                                                    label="Confirm Password"
                                                    name="passwordConfirm"
                                                    onChange={handleChange}
                                                    value={passwordConfirm}
                                                    onBlur={
                                                        validatePasswordOnBlur
                                                    }
                                                    error={
                                                        !passwordConfirmValidation &&
                                                        passwordConfirm !==
                                                            newPassword &&
                                                        passwordConfirm !== ''
                                                    }
                                                    helperText={
                                                        !passwordConfirmValidation &&
                                                        passwordConfirm !==
                                                            newPassword &&
                                                        passwordConfirm !== ''
                                                            ? 'Passwords do not match'
                                                            : ''
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={12}
                                            style={{ marginTop: 10 }}
                                        >
                                            <Grid
                                                container
                                                spacing={2}
                                                sx={{
                                                    alignContent: 'center',
                                                }}
                                            >
                                                <Grid item xs={12} md={8}>
                                                    <Button
                                                        startIcon={<SaveIcon />}
                                                        disabled={
                                                            passwordSubmitDisabled
                                                        }
                                                        onClick={
                                                            handlePasswordSubmit
                                                        }
                                                        variant="contained"
                                                        sx={{ width: '100%' }}
                                                        title="Save new password"
                                                    >
                                                        SAVE
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Button
                                                        startIcon={
                                                            <RestartAltIcon />
                                                        }
                                                        onClick={
                                                            resetPasswordFields
                                                        }
                                                        disabled={
                                                            newPassword?.length ===
                                                                0 ||
                                                            passwordConfirm?.length ===
                                                                0
                                                        }
                                                        variant="contained"
                                                        color="error"
                                                        sx={{ width: '100%' }}
                                                        title="Reset password"
                                                    >
                                                        RESET
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </UniversalAccordion>
                            </FormSection>
                        )}
                    </Grid>
                    {auth?.userProfile?.superAdmin &&
                        auth?.userProfile?.superAdmin === true && (
                            <Grid item xs={12} md={5}>
                                <FormSection
                                    title="User Profile"
                                    titleAlignment="center"
                                >
                                    <Grid item xs={12}>
                                        <UniversalAutocomplete
                                            options={userProfiles.map(
                                                ({ userProfileName }) =>
                                                    userProfileName
                                            )}
                                            value={userProfile?.userProfileName}
                                            handleChange={handleChange}
                                            required={true}
                                            label="User Profile"
                                            name="userProfile"
                                            onBlur={validateOnBlur}
                                            disabled={
                                                !auth?.userProfile?.superAdmin
                                            }
                                            error={
                                                userProfileValidation &&
                                                !userProfile?.userProfileName
                                            }
                                            helperText={
                                                userProfileValidation &&
                                                !userProfile?.userProfileName
                                                    ? 'Please select a user profile.'
                                                    : ''
                                            }
                                            clearFunction={() =>
                                                setState({
                                                    formValidation: {
                                                        ...formValidation,
                                                        isFormDirty: true,
                                                    },
                                                    formData: {
                                                        ...formData,
                                                        userProfile: {},
                                                        activeManufacturer: '',
                                                    },
                                                })
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        {userProfile?.dealerProfile ===
                                            true && (
                                            <Autocomplete
                                                id="assignedDealerProfile"
                                                value={assignedDealerProfile}
                                                onBlur={() =>
                                                    setState({
                                                        formData: {
                                                            ...formData,
                                                            activeManufacturer:
                                                                '',
                                                        },
                                                    })
                                                }
                                                onChange={(event, newValue) => {
                                                    const data = {
                                                        target: {
                                                            name: 'assignedDealerProfile',
                                                            value: newValue,
                                                        },
                                                    }
                                                    handleChange(data)
                                                }}
                                                options={dealerProfileOptions}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        name="assignedDealerProfile"
                                                        label="Assigned Dealer Profile"
                                                        placeholder="Assigned Dealer Profile"
                                                        onBlur={validateOnBlur}
                                                        helperText={
                                                            assignedDealerValidation &&
                                                            !assignedDealerProfile
                                                                ? 'Please select a Dealer Profile'
                                                                : ''
                                                        }
                                                        error={
                                                            assignedDealerValidation &&
                                                            !assignedDealerProfile
                                                        }
                                                        required={true}
                                                    />
                                                )}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        {userProfile?.userProfileName && (
                                            <UniversalAutocomplete
                                                options={
                                                    assignedManufacturersOptions ||
                                                    []
                                                }
                                                disabled={
                                                    userFormPermissionCheck ||
                                                    userProfile?.dealerProfile
                                                        ? !assignedDealerProfile
                                                        : false
                                                }
                                                value={activeManufacturer}
                                                handleChange={handleChange}
                                                onBlur={validateOnBlur}
                                                name="activeManufacturer"
                                                label="Active Manufacturer"
                                                required={true}
                                                error={
                                                    activeManufacturerValidation &&
                                                    !activeManufacturer
                                                }
                                                helperText={
                                                    activeManufacturerValidation &&
                                                    !activeManufacturer
                                                        ? 'Please select an Active Manufacturer.'
                                                        : ''
                                                }
                                            />
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sx={{ marginTop: -8 }}>
                                        {userProfile &&
                                            userProfile?.userProfileName && (
                                                <UserProfileForm
                                                    row={userProfile}
                                                    onClose={() => {}}
                                                    readOnly={true}
                                                    hideDisplay={
                                                        userProfile?.dealerProfile
                                                    }
                                                />
                                            )}
                                    </Grid>
                                </FormSection>
                            </Grid>
                        )}
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <SaveButton
                    handleSubmit={handleSubmit}
                    handleReset={handleReset}
                    disabledSave={!isFormValid || phoneNumberError}
                    disabledReset={!isFormDirty}
                    saveTitle={!userAccount ? 'Save & Close' : 'Save'}
                />
            </Grid>
        </Grid>
    )
}

export default UserForm
